/**
 * Everything the design asks for and the web does not do yet, in one place: each «Pendent» chip
 * on a page names an entry here, and each entry names the roadmap step it belongs to. The chips
 * show testers the scope of what is planned (the owner, 2026-10-12), so they must not drift:
 * `plannedRegistry.test.ts` fails when a chip names no entry, when an entry is drawn nowhere,
 * when a step is not one of R3 to R6 or when a `roadmapItem` is not an item of
 * `FULL_DE_RUTA.md`.
 *
 * A feature added to the roadmap gets an entry here and a chip where it will live; a feature
 * built or dropped loses both. This file is not called `planned.ts` because `Planned.tsx` is
 * its neighbour and two modules cannot differ only by case
 * (`.ai/memory/two-modules-cannot-differ-only-by-case.md`).
 */
export interface PlannedEntry {
  /** What somebody will be able to do, in Catalan. A `Planned` card prints it as its line. */
  label: string;
  /**
   * Why it does not work yet, in one Catalan sentence a player reads: what is missing (the panel,
   * a proof in game, a panel of the designer, a piece nobody has built). «Què ve» prints it under
   * the label (the owner, test card 166, 2026-10-17: the page must say why each one waits).
   */
  reason: string;
  /** A shorter wording for a chip that carries its own name, where the label would not fit. */
  chip?: string;
  /** The step of docs/design/ROADMAP.md §10 it belongs to: R3 to R6, with or without a sub-step. */
  step: string;
  /** The matching item of FULL_DE_RUTA.md, when the player-facing roadmap has one. */
  roadmapItem?: string;
  /** Why it waits, beyond not being built: the panel must give it, or it needs proving in game. */
  why?: 'panel' | 'ingame';
}

/** `satisfies` keeps the keys literal, so `PlannedId` is the union of the ids and a typo fails tsc. */
export const PLANNED = {
  // --- R3: the session and the direction, from the phone ---
  'sessions-reports-one-page': {
    label: 'Sessions i informes en una sola pàgina',
    reason:
      'Ara les sessions i els informes de partida es consulten per separat; ajuntar-los en una sola fitxa per nit està previst per a més endavant.',
    step: 'R3.1',
    roadmapItem: 'sessions-i-informes',
  },
  'session-map-marker': {
    label: 'Marcador amb pressió llarga, visible només al meu bàndol',
    reason:
      'Cal un camí perquè la web posi marques dins del joc només per al teu bàndol, i encara no s’ha provat en una partida.',
    step: 'R3.1',
    roadmapItem: 'marca-propia-des-del-mobil',
    why: 'ingame',
  },
  'session-map-positions': {
    label: 'Posicions dels jugadors i de les esquadres amigues',
    reason:
      'El joc encara no envia a la web on és cada jugador, i caldrà provar-ho en una partida real.',
    step: 'R3.3',
    roadmapItem: 'posicions-al-mapa',
    why: 'ingame',
  },
  'roster-squads': {
    label: 'Esquadres com a targetes, amb el color de l’equip i els avatars',
    reason: 'El panell encara no dona les esquadres de cada partida en directe.',
    step: 'R3.3',
    roadmapItem: 'sessio-al-mobil',
    why: 'panel',
  },
  'roster-wounded': {
    label: 'Ferit o reviscolant, al moment, del panell',
    reason: 'El panell encara no diu en directe qui està ferit o reviscolant.',
    step: 'R3.3',
    roadmapItem: 'sessio-al-mobil',
    why: 'panel',
  },
  'roster-positions': {
    label: 'Posicions dels jugadors al mapa',
    reason: 'El panell encara no dona les posicions dels jugadors durant la partida.',
    step: 'R3.3',
    roadmapItem: 'posicions-al-mapa',
    why: 'panel',
  },
  'direction-ai-density': {
    label: 'Veure la densitat d’IA al mapa de la direcció',
    reason:
      'Cal que el joc enviï on és la IA durant la partida, i encara no s’ha provat en una partida real.',
    step: 'R3.2',
    roadmapItem: 'dirigir-des-del-mobil',
    why: 'ingame',
  },
  'direction-positions': {
    label: 'Veure les posicions dels jugadors al mapa de la direcció',
    reason: 'El panell encara no dona les posicions dels jugadors durant la partida.',
    step: 'R3.3',
    roadmapItem: 'posicions-al-mapa',
    why: 'panel',
  },
  'direction-support-usage': {
    label: 'Saber qui ha demanat cada suport, quan i on',
    reason:
      'El joc encara no diu qui demana cada suport ni des d’on, i caldrà provar-ho en una partida real.',
    step: 'R3.2',
    roadmapItem: 'dirigir-des-del-mobil',
    why: 'ingame',
  },
  'objective-assign-extend': {
    label: 'Assignar un objectiu i ampliar-ne el temps',
    reason: 'La direcció encara no té l’ordre que ho fa dins del joc.',
    chip: 'Assigna · Amplia el temps',
    step: 'R3.2',
    roadmapItem: 'dirigir-des-del-mobil',
  },
  'direction-schedule-message': {
    label: 'Programar un missatge de comandament',
    reason: 'La direcció encara no pot deixar una ordre preparada perquè surti més tard.',
    step: 'R3.2',
    roadmapItem: 'dirigir-des-del-mobil',
  },
  'direction-undo': {
    label: 'Desfer una ordre abans que arribi al joc',
    reason:
      'Les ordres de la direcció surten cap al joc de seguida; encara no hi ha cap espera que permeti desfer-les.',
    step: 'R3.2',
    roadmapItem: 'dirigir-des-del-mobil',
  },
  'direction-hc-fps': {
    label: 'Veure els FPS del headless client',
    reason:
      'El joc encara no envia el rendiment del headless client, i caldrà provar-ho en una partida real.',
    step: 'R3.2',
    roadmapItem: 'client-sense-cap',
    why: 'ingame',
  },
  'direction-scheduler-lag': {
    label: 'Veure el retard de l’escalonador del servidor',
    reason:
      'El joc encara no envia aquesta mesura del servidor, i caldrà provar-ho en una partida real.',
    step: 'R3.2',
    roadmapItem: 'dirigir-des-del-mobil',
    why: 'ingame',
  },

  // --- R4: making missions ---
  'designer-generate-whole': {
    label: 'Generar tota la missió des del dissenyador',
    reason:
      'El dissenyador ja té tots els panells, però encara no hi ha cap botó que generi la missió sencera d’una vegada; de moment es genera des de l’assistent.',
    step: 'R4.1',
    roadmapItem: 'dissenyador',
  },
  'designer-preview': {
    label: 'Previsualitzar la missió abans de publicar-la',
    reason:
      'Encara no hi ha una vista prèvia de la missió sencera; cada panell del dissenyador es veu per separat.',
    step: 'R4.1',
    roadmapItem: 'dissenyador',
  },
  'wizard-open-designer': {
    label: 'Obrir el dissenyador complet des de l’últim pas de l’assistent',
    reason:
      'El dissenyador ja està complet, però l’assistent encara no té cap enllaç per passar-hi des de l’últim pas.',
    step: 'R4',
    roadmapItem: 'dissenyador',
  },
  'library-import': {
    label: 'Importar una missió feta a mà',
    reason:
      'Encara no hi ha cap manera de llegir una missió feta a mà i convertir-la en una missió d’aquí.',
    step: 'R4',
    roadmapItem: 'importar-missions',
  },
  'mission-own-cover': {
    label: 'Triar una imatge pròpia com a portada de la missió',
    reason:
      'Ara la portada surt del terreny o del tipus de missió; pujar-ne una de pròpia encara no s’ha fet.',
    chip: 'Portada pròpia',
    step: 'R4',
    roadmapItem: 'portada-propia',
  },
  'briefing-print': {
    label: 'Impressió / PDF pel camí del panell',
    reason:
      'La impressió anirà a través del panell, que encara no ofereix aquest camí per al briefing.',
    step: 'R4',
    roadmapItem: 'briefing-per-seccions',
  },

  // --- R5: campaigns ---
  'vote-map': {
    label: 'Posar el mapa a votació',
    reason: 'Les votacions encara no tenen aquest tipus de pregunta.',
    step: 'R5.2',
    roadmapItem: 'votar-mapa-enemic-i-tipus',
  },
  'vote-enemy': {
    label: 'Posar l’enemic a votació',
    reason: 'Les votacions encara no tenen aquest tipus de pregunta.',
    step: 'R5.2',
    roadmapItem: 'votar-mapa-enemic-i-tipus',
  },
  'vote-mission-type': {
    label: 'Posar el tipus de missió a votació',
    reason: 'Les votacions encara no tenen aquest tipus de pregunta.',
    step: 'R5.2',
    roadmapItem: 'votar-mapa-enemic-i-tipus',
  },
  'vote-next-chapter': {
    label: 'Votar el capítol següent',
    reason: 'La votació està feta, però encara no s’ha provat en una campanya real.',
    step: 'R5.2',
    roadmapItem: 'votar-el-capitol',
  },
  'campaign-front-line': {
    label: 'La línia del front com a element mogut per la comunitat',
    reason:
      'Les campanyes per capítols encara no s’han provat en partides reals, i la línia del front encara no es dibuixa.',
    step: 'R5.2',
    roadmapItem: 'campanyes-per-capitols',
  },
  'campaign-cast-pins': {
    label: 'Els pins del repartiment sobre el mapa, amb el seu estat',
    reason:
      'Les campanyes per capítols encara no s’han provat en partides reals, i els pins encara no es dibuixen al mapa.',
    step: 'R5.2',
    roadmapItem: 'campanyes-per-capitols',
  },
  'campaign-next-session': {
    label: 'La targeta de la pròxima sessió, amb la votació del capítol següent',
    reason:
      'Depèn de la votació del capítol següent, que encara no s’ha provat en una campanya real.',
    step: 'R5.2',
    roadmapItem: 'votar-el-capitol',
  },
  'library-campaigns': {
    label: 'Les campanyes de diversos capítols, a la biblioteca',
    reason:
      'Les campanyes de diversos capítols encara no s’han provat en partides reals; de moment no serveixen per a res als jugadors.',
    chip: 'Campanyes',
    step: 'R5',
    roadmapItem: 'campanyes-per-capitols',
  },
  'profile-squad-role': {
    label: 'Esquadra i rol de cada sessió',
    reason: 'La plataforma encara no guarda en quina esquadra i amb quin rol va jugar cadascú.',
    step: 'R5.1',
    roadmapItem: 'perfil-complet',
  },
  'profile-campaign-rank': {
    label: 'Rang de la campanya',
    reason: 'Depèn de les campanyes per capítols, que encara no s’han provat en partides reals.',
    step: 'R5.1',
    roadmapItem: 'perfil-complet',
  },

  // --- R6: ready for everybody ---
  'server-difficulty': {
    label: 'Triar la dificultat del servidor',
    reason:
      'Aquesta opció encara no s’ha afegit al formulari del servidor; de moment es canvia al panell de servidors.',
    step: 'R6.1',
    roadmapItem: 'fitxa-de-servidor-completa',
  },
  'server-advanced-parameters': {
    label: 'Escriure paràmetres avançats del servidor',
    reason:
      'Aquesta opció encara no s’ha afegit al formulari del servidor; de moment es canvia al panell de servidors.',
    step: 'R6.1',
    roadmapItem: 'fitxa-de-servidor-completa',
  },
  'servers-mod-fingerprint': {
    label:
      'Empremta del conjunt de mods i insígnia «cal reiniciar» quan el que corre no coincideix amb el configurat.',
    reason: 'El panell encara no diu quins mods corren de debò al servidor.',
    step: 'R6.1',
    roadmapItem: 'fitxa-de-servidor-completa',
    why: 'panel',
  },
  'servers-probe-card': {
    label: 'El servidor de prova com a targeta pròpia quan la resposta l’identifica.',
    reason: 'El panell encara no diu quin dels servidors és el de prova.',
    step: 'R6.1',
    roadmapItem: 'fitxa-de-servidor-completa',
    why: 'panel',
  },
  'settings-password-recovery': {
    label: 'Recuperació de la contrasenya per una identitat enllaçada o per correu',
    reason:
      'Ara només un administrador et pot posar una contrasenya nova; recuperar-la tu mateix encara no s’ha fet.',
    step: 'R6.1',
    roadmapItem: 'recuperar-contrasenya',
  },
} as const satisfies Record<string, PlannedEntry>;

export type PlannedId = keyof typeof PLANNED;

/** The entry of an id, widened: the optional fields are there to read. */
export function plannedEntry(id: PlannedId): PlannedEntry {
  return PLANNED[id];
}

/** The stage of the player-facing roadmap a step belongs to: R3.2 is stage `R3`. */
export type PlannedStage = 'R3' | 'R4' | 'R5' | 'R6';

export function stageOf(step: string): PlannedStage {
  return step.slice(0, 2) as PlannedStage;
}

/** Where the chip leads a signed-in reader: the roadmap page, at the item when there is one. */
export function roadmapLink(id: PlannedId): string {
  const item = plannedEntry(id).roadmapItem;
  return item ? `/full-de-ruta#${item}` : '/full-de-ruta';
}
